<template>
  <div class="dashboard-container">
    <header class="dashboard-header">
      <h1 v-if="headerConfig.show_titles">{{ tConfig(headerConfig.title, lang) }}</h1>
      <p v-if="headerConfig.show_titles">{{ tConfig(headerConfig.subtitle, lang) }}</p>

      <div class="tabs-container">
        <button
          v-for="screen in screens.filter(s => s.active !== false)"
          :key="screen.id"
          class="tab-button"
          :class="{ active: activeScreenId === screen.id }"
          @click="activeScreenId = screen.id"
        >
          {{ tConfig(screen.title, lang) }}
        </button>
        <button v-if="isPdfExportEnabled" class="pdf-btn" @click="openPdfExportModal" :title="t('exportPdf', lang)">📄</button>
        <button v-if="isEditable" class="config-btn" @click="$emit('enter-config', { screenId: activeScreenId })" :title="t('openConfig', lang)">⚙</button>
      </div>
    </header>

    <div class="dashboard-content">
      <div class="kpi-grid" :class="{ 'kpi-single-row': activeScreen?.kpiSingleRow }" v-if="kpiWidgets.length > 0">
        <template v-for="widget in kpiWidgets" :key="widget.id">
          <div v-if="widget.params.lineBreak" class="kpi-line-break"></div>
          <div class="kpi-card-wrapper" style="position: relative;">
            <button
              v-if="isEditable"
              class="widget-config-btn"
              @click.stop="$emit('open-widget-config', { screenId: activeScreenId, widgetId: widget.id })"
              title="Configurar widget"
            >⚙</button>
            <WidgetRenderer
              :widget="widget"
              :lang="lang"
              :kpi-value="kpiValue(widget)"
              :kpi-metric="kpiMetric(widget)"
            />
          </div>
        </template>
      </div>

      <div v-if="apiError" class="api-error">
        <span class="api-error-icon">⚠️</span>
        <span class="api-error-msg">{{ apiError }}</span>
      </div>

      <div v-if="loading" class="loading-overlay">
        <spinner-loading :size="36" :line-size="3" line-fg-color="#d42e12" />
      </div>

      <div v-else class="grid-layout">
        <div
          v-for="widget in otherWidgets"
          :key="widget.id"
          class="widget-wrapper"
          :class="{
            'full-width': widget.width === 'full',
            'half-width': widget.width === 'half',
            'third-width': widget.width === 'third',
            'two-thirds-width': widget.width === 'two-thirds',
            'quarter-width': widget.width === 'quarter'
          }"
        >
          <button
            v-if="isEditable"
            class="widget-config-btn"
            @click.stop="$emit('open-widget-config', { screenId: activeScreenId, widgetId: widget.id })"
            title="Configurar widget"
          >⚙</button>

          <div v-if="widget.params.drilldown" class="drill-toolbar">
            <div
              v-if="(getDisplayMode(widget) === 'drill' || getDisplayMode(widget) === 'cumulative') && drillStates[widget.id]?.length"
              class="drill-breadcrumb"
            >
              <button class="drill-crumb" @click="drillUp(widget, 0)">⌂</button>
              <template v-for="(step, i) in drillStates[widget.id]" :key="i">
                <span class="drill-sep">›</span>
                <button class="drill-crumb" @click="drillUp(widget, i + 1)">{{ step.value }}</button>
              </template>
            </div>
            <div class="drill-mode-switch">
              <button
                :class="{ active: getDisplayMode(widget) === 'drill' }"
                @click="setDisplayMode(widget, 'drill')"
              >{{ t('drillMode', lang) }}</button>
              <button
                :class="{ active: getDisplayMode(widget) === 'multi' }"
                @click="setDisplayMode(widget, 'multi')"
              >{{ t('multiMode', lang) }}</button>
              <button
                :class="{ active: getDisplayMode(widget) === 'cumulative' }"
                @click="setDisplayMode(widget, 'cumulative')"
              >{{ t('cumulativeMode', lang) }}</button>
            </div>
          </div>

          <WidgetRenderer
            :widget="widget"
            :lang="lang"
            :editable="isEditable ?? true"
            :options="getMergedOptions(widget)"
            :series="widgetSeries(widget)"
            :data-node="chartData[widget.dataSourceKey]"
            :meta-labels="metaLabelCache[widget.params?.segmentId] || {}"
            @data-point-selection="(config) => handleChartClick(widget, config)"
            @export-table="exportTableToExcel(widget)"
          />
        </div>
      </div>
    </div>

    <PdfExportModal :visible="showPdfModal" :lang="lang" @export="exportToPDF" @close="showPdfModal = false" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import WidgetRenderer from '../components/WidgetRenderer.vue';
import PdfExportModal from '../components/PdfExportModal.vue';
import { getEffectiveConfig } from '../data/dashboardConfig';
import api, { setToken, setBaseUrl } from '../services/api';
import { t, tConfig } from '../i18n/index.js';
import { setDebug } from '../utils/debug.js';
import {
  getNestedProperty,
  getEffectiveWidget
} from '../utils/widgetTransform.js';
import { buildMergedOptions } from '../composables/useWidgetOptions.js';
import { useWidgetData } from '../composables/useWidgetData.js';

const props = defineProps({
  initialScreenId: { type: String, default: null },
  token: { type: String, default: null },
  showHeader: { type: Boolean, default: null },
  lang: { type: String, default: 'ca_es' },
  apiBaseUrl: { type: String, default: null },
  screens: { type: Array, default: null },
  editable: { type: Boolean, default: false },
  pdfExport: { type: Boolean, default: false },
  title: { type: [String, Object], default: null },
  subtitle: { type: [String, Object], default: null },
  debug: { type: Boolean, default: false }
});
const { initialScreenId } = props;

setToken(props.token || null);
setBaseUrl(props.apiBaseUrl || null);
setDebug(props.debug);

watch(() => props.debug, val => setDebug(val));

watch(() => props.token, (val) => { setToken(val || null); fetchData(); });
watch(() => props.apiBaseUrl, (val) => { setBaseUrl(val || null); });

defineEmits(['enter-config', 'open-widget-config']);

const { screens: effectiveScreens } = getEffectiveConfig(props.screens);
const chartData = ref({});
const screens = ref(effectiveScreens);
const activeScreenId = ref(
  initialScreenId ||
  screens.value.find(s => s.active === true)?.id ||
  screens.value[0]?.id ||
  ''
);
const loading = ref(true);
const error = ref(null);
// Fetch + shape per widget + cache de metadata del segment (compartit amb el preview
// de l'editor). metaLabelCache l'usen kpiMetric i el prop meta-labels del renderer.
const { metadataCache, metaLabelCache, ensureMetadata, fetchWidget } = useWidgetData({ lang: props.lang });
const headerConfig = ref({
  show_titles: props.showHeader !== null ? props.showHeader : true,
  editable: props.editable,
  pdf_export: props.pdfExport,
  title: props.title,
  subtitle: props.subtitle
});
const showPdfModal = ref(false);
const apiError = ref(null);
const crossFilters = ref({});
// Filtres del mode interactiu global (pantalla amb `interactive: true`):
// { dimensió: valor } compartit per tots els gràfics de la pantalla.
const globalFilters = ref({});
const drillStates = ref({});
const displayModes = ref({});

function getDisplayMode(widget) {
  const drilldown = widget.params?.drilldown;
  if (!Array.isArray(drilldown) || drilldown.length === 0) return 'drill';
  return displayModes.value[widget.id] || 'drill';
}

function setDisplayMode(widget, mode) {
  if (getDisplayMode(widget) === mode) return;
  displayModes.value = { ...displayModes.value, [widget.id]: mode };
  if (mode !== 'drill') {
    drillStates.value = { ...drillStates.value, [widget.id]: [] };
  }
  delete chartData.value[widget.dataSourceKey];
  fetchData();
}

// Widget efectiu segons el mode/drill actuals d'aquesta instància (wrapper del
// helper pur getEffectiveWidget, que rep mode+drillPath per arguments).
function effectiveWidget(widget) {
  return getEffectiveWidget(widget, {
    mode: getDisplayMode(widget),
    drillPath: drillStates.value[widget.id] || []
  });
}

function drillUp(widget, level) {
  const path = drillStates.value[widget.id] || [];
  const newPath = path.slice(0, level);
  drillStates.value = { ...drillStates.value, [widget.id]: newPath };
  delete chartData.value[widget.dataSourceKey];
  fetchData();
}

function detectApiError (err) {
  if (!api.defaults.baseURL) {
    apiError.value = t('errorNoBaseUrl', props.lang);
    return true;
  }
  const status = err?.response?.status;
  if (status === 401) {
    apiError.value = t('errorUnauthorized', props.lang);
    return true;
  }
  return false;
}

watch(() => props.showHeader, (val) => { if (val !== null) headerConfig.value.show_titles = val; });
watch(() => props.editable,  (val) => { headerConfig.value.editable = val; });
watch(() => props.pdfExport, (val) => { headerConfig.value.pdf_export = val; });
watch(() => props.title,     (val) => { headerConfig.value.title = val; });
watch(() => props.subtitle,  (val) => { headerConfig.value.subtitle = val; });

const activeScreen = computed(() => screens.value.find(s => s.id === activeScreenId.value) || screens.value[0]);
const kpiWidgets = computed(() => activeScreen.value?.widgets.filter(w => w.type === 'kpi' && w.active !== false) || []);
const otherWidgets = computed(() => activeScreen.value?.widgets.filter(w => w.type !== 'kpi' && w.active !== false) || []);
const isEditable = computed(() => headerConfig.value?.editable ?? true);
const isPdfExportEnabled = computed(() => headerConfig.value?.pdf_export ?? true);

// Total de VIPs del segment: el pren del KPI total (kpi sense kpiPercentOf, p.ex.
// professionLanguage que cobreix tots els VIPs). Serveix de denominador per als
// KPIs de percentatge.
const isPercentKpi = (w) =>
  w.params?.kpiPercentAny === true ||
  (w.params?.kpiPercentOf !== undefined && w.params?.kpiPercentOf !== null);

const totalVips = computed(() => {
  for (const s of screens.value) {
    for (const w of (s.widgets || [])) {
      if (w.type === 'kpi' && !isPercentKpi(w)) {
        const v = chartData.value[w.dataSourceKey]?.[w.params.valueKey];
        if (typeof v === 'number' && v > 0) return v;
      }
    }
  }
  return 0;
});

// Valor a mostrar al KpiWidget: total directe o, si és KPI de percentatge,
// numerador / total de VIPs formatat amb "%".
function kpiValue(widget) {
  const raw = chartData.value[widget.dataSourceKey]?.[widget.params.valueKey];
  if (!isPercentKpi(widget)) return raw ?? 0;
  const matched = Number(raw) || 0;
  const denom = totalVips.value;
  const pct = denom > 0 ? (matched / denom) * 100 : 0;
  return pct.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '%';
}

// Agregada opcional de un KPI. Toma la 1ª métrica (una sola tiene sentido en un KPI) y su
// 1ª función; el valor viene del total de scope que devuelve el backend (metricTotals), no
// de los buckets (para que AVG/MIN/MAX sean globales correctos). Etiqueta = título de la
// proyección FUNC(campo) resuelto por servicios (metaLabelCache) o genérico.
const KPI_FUNC_LABELS = { SUM: 'Suma', AVG: 'Media', MIN: 'Mínimo', MAX: 'Máximo', MEDIAN: 'Mediana' };
function kpiMetric(widget) {
  const none = { value: null, label: '', only: false };
  const w = effectiveWidget(widget);
  // kpiHideMetric: el KPI usa metrics per calcular el valor per bucket (mapping.value =
  // FUNC(camp)) però NO vol mostrar la línia secundària d'agregada global (metricTotals).
  if (w.params?.kpiHideMetric) return none;
  const metrics = Array.isArray(w.params?.metrics) ? w.params.metrics : null;
  if (!metrics || !metrics.length) return none;
  const m = metrics[0];
  const func = Array.isArray(m.funcs) ? m.funcs[0] : null;
  if (!m || !m.field || !func) return none;
  const totals = chartData.value[w.dataSourceKey]?.metricTotals || {};
  const key = `${func}(${m.field})`;
  const value = totals[key];
  if (value === undefined || value === null) return none;
  const labelByKey = metaLabelCache.value[w.params?.segmentId] || {};
  const clean = key.replace(/[\[\]" ]/g, '').toLowerCase();
  const label = labelByKey[clean] || (KPI_FUNC_LABELS[func] || func);
  return { value, label, only: !!w.params?.kpiMetricOnly };
}

// Sèrie d'un gràfic. Per als gràfics de presència (params.presence) es calcula
// en temps de render: [amb info, total − amb info], amb el total del segment
// (totalVips) que ja està carregat quan es pinta. Per a la resta, la sèrie ja
// ve calculada de transformApiData.
function widgetSeries(widget) {
  const node = chartData.value[widget.dataSourceKey];
  if (!node) return [];
  if (node.presence) {
    const con = Number(node.conInfo) || 0;
    const total = totalVips.value || 0;
    return [con, Math.max(total - con, 0)];
  }
  return node.series || [];
}

watch(activeScreenId, () => fetchData());

onMounted(() => fetchData());

// Assegura el metadata d'un segment; el composable propaga l'error i aquí hi apliquem
// detectApiError (URL base / 401) com feia l'antic ensureSegmentMetadata.
async function ensureSegmentMetadata(segmentId) {
  try {
    return await ensureMetadata(segmentId);
  } catch (err) {
    detectApiError(err);
    return {};
  }
}

async function fetchData() {
  loading.value = true;
  error.value = null;
  apiError.value = null;

  if (!chartData.value) chartData.value = {};

  const widgets = activeScreen.value?.widgets || [];
  const segments = [...new Set(widgets.map(w => w.params?.segmentId).filter(id => !!id))];
  await Promise.all(segments.map(id => ensureSegmentMetadata(id)));

  const fetchPromises = widgets.map(async (rawWidget) => {
    if (!rawWidget.dataSourceKey || !rawWidget.params?.segmentId) return;
    if (chartData.value[rawWidget.dataSourceKey]) return;

    const widget = effectiveWidget(rawWidget);
    const drillPath = drillStates.value[rawWidget.id] || [];
    // En mode cumulative cada nivell afegeix una dimensió però no filtra:
    // volem veure totes les categories del nivell superior desplegades.
    const isCumulative = getDisplayMode(rawWidget) === 'cumulative';
    const drillFilter = isCumulative
      ? {}
      : drillPath.reduce((acc, p) => { acc[p.field] = String(p.value); return acc; }, {});

    // Mode interactiu: apliquem tots els filtres globals excepte el de la pròpia
    // dimensió (perquè el gràfic font conservi totes les categories).
    const dimensionParam = widget.params.dimension;
    const baseDimNames = Array.isArray(dimensionParam) ? dimensionParam : (dimensionParam ? [dimensionParam] : []);
    let interactiveFilter = {};
    if (activeScreen.value?.interactive) {
      const ownLower = baseDimNames.map(d => String(d).toLowerCase());
      interactiveFilter = Object.fromEntries(
        Object.entries(globalFilters.value).filter(([f]) => !ownLower.includes(String(f).toLowerCase()))
      );
    }
    // staticFilter: filtre fix declarat al widget (p.ex. { categoryLanguage: 'Consumidor' }).
    const staticFilter = widget.params?.staticFilter || {};
    const baseFilter = { ...(crossFilters.value[widget.dataSourceKey] || {}), ...interactiveFilter, ...staticFilter };
    const activeFilter = (Object.keys(baseFilter).length || Object.keys(drillFilter).length)
      ? { ...baseFilter, ...drillFilter }
      : null;

    try {
      const node = await fetchWidget(widget, { filters: activeFilter, cumulative: isCumulative });
      chartData.value[widget.dataSourceKey] = node;
    } catch (err) {
      detectApiError(err);
    }
  });

  try {
    await Promise.all(fetchPromises);
  } catch (err) {
    error.value = "Alguns gràfics no s'han pogut carregar.";
  } finally {
    loading.value = false;
  }
}

function exportTableToExcel(widget) {
  const rows = chartData.value[widget.dataSourceKey]?.data || [];
  const cols = widget.params.columns || [
    { field: widget.params.mapping?.category, label: widget.params.mapping?.category },
    { field: widget.params.mapping?.value, label: widget.params.mapping?.value }
  ].filter(c => c.field);
  const data = rows.map(item => {
    const row = {};
    cols.forEach(col => {
      const raw = getNestedProperty(item, col.field) ?? item[col.field];
      row[tConfig(col.label, props.lang)] = raw ?? '';
    });
    return row;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  const base = tConfig(widget.params.title?.text || widget.params.title, props.lang) || 'taula';
  XLSX.writeFile(wb, String(base).replace(/[^\w]+/g, '_') + '.xlsx');
}

function getMergedOptions(rawWidget) {
  const widget = effectiveWidget(rawWidget);
  return buildMergedOptions(widget, chartData.value[widget.dataSourceKey], {
    lang: props.lang,
    crossFilters: crossFilters.value,
    globalFilters: globalFilters.value,
    interactive: !!activeScreen.value?.interactive,
    screenLegendMaxChars: activeScreen.value?.legendMaxChars,
    metaLabels: metaLabelCache.value[widget.params?.segmentId] || {}
  });
}

function handleChartClick(widget, config) {
  if (!config) return;

  // Mode interactiu global: qualsevol gràfic filtra tots els altres per la
  // seva dimensió (toggle). S'activa amb `interactive: true` a la pantalla.
  if (activeScreen.value?.interactive && !widget?.params?.drilldown) {
    const dimParam = widget.params?.dimension;
    const field = Array.isArray(dimParam) ? dimParam[0] : dimParam;
    const data = chartData.value[widget.dataSourceKey];
    const value = data?.categories?.[config.dataPointIndex] ?? data?.labels?.[config.dataPointIndex];
    if (!field || value === undefined || value === null) return;
    const next = { ...globalFilters.value };
    if (String(next[field]) === String(value)) delete next[field];
    else next[field] = String(value);
    globalFilters.value = next;
    chartData.value = {};
    fetchData();
    return;
  }

  const drilldown = widget?.params?.drilldown;
  const mode = getDisplayMode(widget);

  if (Array.isArray(drilldown) && drilldown.length > 0 && mode === 'drill') {
    const path = drillStates.value[widget.id] || [];
    if (path.length >= drilldown.length - 1) return;
    const data = chartData.value[widget.dataSourceKey];
    const value = data?.categories?.[config.dataPointIndex] || data?.labels?.[config.dataPointIndex];
    if (value === undefined || value === null) return;
    const currentDim = drilldown[path.length];
    drillStates.value = {
      ...drillStates.value,
      [widget.id]: [...path, { field: currentDim, value: String(value) }]
    };
    delete chartData.value[widget.dataSourceKey];
    fetchData();
    return;
  }

  if (Array.isArray(drilldown) && drilldown.length > 0 && mode === 'cumulative') {
    const path = drillStates.value[widget.id] || [];
    if (path.length >= drilldown.length - 1) return;
    // En mode cumulative qualsevol clic desplega el següent nivell per a totes
    // les categories: no filtrem pel valor de la barra clicada.
    const currentDim = drilldown[path.length];
    const label = currentDim.split('.').pop();
    drillStates.value = {
      ...drillStates.value,
      [widget.id]: [...path, { field: currentDim, value: label }]
    };
    delete chartData.value[widget.dataSourceKey];
    fetchData();
    return;
  }

  if (!widget?.params?.crossfilter) return;
  const cf = widget.params.crossfilter;
  const data = chartData.value[widget.dataSourceKey];
  const value = data?.categories?.[config.dataPointIndex] || data?.labels?.[config.dataPointIndex];
  if (value === undefined || value === null) return;

  const current = crossFilters.value[cf.target]?.[cf.field];
  if (current === String(value)) {
    delete crossFilters.value[cf.target];
  } else {
    crossFilters.value[cf.target] = { [cf.field]: String(value) };
  }

  delete chartData.value[cf.target];
  fetchData();
}

function openPdfExportModal() {
  showPdfModal.value = true;
}

async function exportToPDF(exportOptions) {
  showPdfModal.value = false;

  // Esperar un tick perquè el modal desaparegui
  await new Promise(resolve => setTimeout(resolve, 200));

  // Guardar elements a amagar temporalment
  const header = document.querySelector('.dashboard-header');
  const configBtns = document.querySelectorAll('.widget-config-btn');
  const widgetHeaders = document.querySelectorAll('.widget-header');
  const apexToolbars = document.querySelectorAll('.apexcharts-toolbar');

  // Amagar capçalera
  if (header) header.style.display = 'none';

  // Amagar botons de configuració
  configBtns.forEach(btn => btn.style.display = 'none');

  // Amagar botons "View Table" / controls
  if (exportOptions.hideButtons) {
    widgetHeaders.forEach(el => el.style.display = 'none');
    apexToolbars.forEach(el => el.style.display = 'none');
  }

  // Amagar KPIs si no es vol
  const kpiGrid = document.querySelector('.kpi-grid');
  if (!exportOptions.includeKpi && kpiGrid) {
    kpiGrid.style.display = 'none';
  }

  // Capturar l'element VIVA del dashboard (no un clon)
  const element = document.querySelector('.dashboard-content');
  if (!element) {
    alert("No s'ha pogut trobar el contingut del dashboard");
    // Restaurar tot
    if (header) header.style.display = '';
    configBtns.forEach(btn => btn.style.display = '');
    widgetHeaders.forEach(el => el.style.display = '');
    apexToolbars.forEach(el => el.style.display = '');
    if (kpiGrid) kpiGrid.style.display = '';
    return;
  }

  // Esperar un moment perquè el DOM s'actualitzi
  await new Promise(resolve => setTimeout(resolve, 500));

  const qualitySettings = {
    low: { scale: 2, quality: 0.75 },
    medium: { scale: 2.5, quality: 0.88 },
    high: { scale: 3, quality: 0.98 }
  };

  const quality = qualitySettings[exportOptions.quality] || qualitySettings.high;
  const fileName = `Dashboard_${activeScreen.value?.title || 'Export'}_${new Date().toISOString().split('T')[0]}.pdf`;

  const options = {
    margin: [10, 10, 10, 10],
    filename: fileName,
    image: { type: 'jpeg', quality: quality.quality },
    html2canvas: {
      scale: quality.scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#f3f4f6',
      scrollX: 0,
      scrollY: 0,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    },
    jsPDF: {
      orientation: exportOptions.orientation,
      unit: 'mm',
      format: 'a4',
      compress: true
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };

  try {
    await html2pdf().set(options).from(element).save();
  } catch (err) {
    console.error('Error exportant PDF:', err);
    alert("Hi ha hagut un error exportant el PDF");
  }

  // Restaurar tot
  if (header) header.style.display = '';
  configBtns.forEach(btn => btn.style.display = '');
  if (exportOptions.hideButtons) {
    widgetHeaders.forEach(el => el.style.display = '');
    apexToolbars.forEach(el => el.style.display = '');
  }
  if (!exportOptions.includeKpi && kpiGrid) {
    kpiGrid.style.display = '';
  }
}
</script>

<style scoped>
.dashboard-container {
  margin: 0;
  overflow-x: hidden;
  font-family: 'Source Sans Pro', sans-serif;
  background-color: transparent;
  padding: 0;
}

.dashboard-header {
  margin-bottom: 0;
  padding: 0 0 10px 0;
}

.dashboard-header h1 {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  padding: 3px 10px;
  margin-bottom: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  line-height: normal;
}

.dashboard-header p {
  color: #666;
  font-size: 17px;
  padding: 3px 10px;
  font-family: 'Source Sans Pro', sans-serif;
  line-height: normal;
  margin-bottom: 2px;
}

.kpi-grid {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .kpi-grid {
    justify-content: center;
  }
}

.kpi-line-break {
  flex-basis: 100%;
  height: 0;
}

/* Mode d'una sola fila: no fer wrap i deixar que les targetes s'encongeixin. */
.kpi-grid.kpi-single-row {
  flex-wrap: nowrap;
  overflow-x: auto;
}
.kpi-grid.kpi-single-row > .kpi-card-wrapper {
  min-width: 0;
}

.kpi-grid > .kpi-card-wrapper {
  flex: 0 1 180px;
}

.grid-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  align-items: stretch;
}

@media (min-width: 1200px) {
  .grid-layout {
    grid-template-columns: repeat(12, 1fr);
  }

  .full-width      { grid-column: span 12; }
  .two-thirds-width { grid-column: span 8; }
  .half-width      { grid-column: span 6; }
  .third-width     { grid-column: span 4; }
  .quarter-width   { grid-column: span 3; }

  .grid-layout > :not(.full-width):not(.half-width):not(.third-width):not(.two-thirds-width):not(.quarter-width) {
    grid-column: span 6;
  }
}

.tabs-container {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 0;
  margin-top: 0;
  border-bottom: 1px dotted #bbbbbb;
  padding-bottom: 0;
  background-color: #efefef;
  max-height: 35px;
  height: 35px;
}

.tab-button {
  background: transparent;
  border: 0;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 15px;
  font-weight: 600;
  text-transform: none;
  color: #000;
  padding: 7px 9px 8px 10px;
  cursor: pointer;
  position: relative;
  line-height: normal;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  transition: text-decoration-color 0.2s ease-in;
  border-radius: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-button:hover {
  text-decoration-color: rgb(212, 46, 18);
}

.tab-button.active {
  text-decoration-color: rgb(212, 46, 18);
}

.tab-button.active::after {
  display: none;
}

.pdf-btn,
.config-btn {
  background: none;
  border: 1px solid var(--sherpa-border, #ddd);
  border-radius: 3px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--sherpa-muted, #888);
  transition: all 0.2s;
  margin-left: auto;
}

.pdf-btn { margin-right: 4px; }

.pdf-btn:hover   { border-color: var(--sherpa-teal, #21B7C9); color: var(--sherpa-teal, #21B7C9); }
.config-btn:hover { border-color: var(--sherpa-red, #d42e12); color: var(--sherpa-red, #d42e12); }

.widget-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.widget-wrapper > *:not(.widget-config-btn):not(.drill-toolbar) {
  flex: 1;
  min-height: 0;
}

.widget-config-btn {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 20;
  background: rgba(212, 46, 18, 0.85);
  color: white;
  border: none;
  border-radius: 3px;
  padding: 3px 7px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-wrapper:hover .widget-config-btn,
.kpi-card-wrapper:hover .widget-config-btn {
  opacity: 1;
}

.widget-config-btn:hover {
  background: #d42e12;
}

@media (max-width: 768px) {
  .widget-config-btn { opacity: 0.8; }
  .grid-layout { grid-template-columns: 1fr; }
}

.api-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #664d03;
}

.api-error-icon { font-size: 18px; flex-shrink: 0; }
.api-error-msg  { line-height: 1.4; }

.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
}

@media print {
  .dashboard-header {
    position: static;
    background: white;
    padding: 20px;
    margin-bottom: 20px;
  }

  .tabs-container {
    display: none;
  }

  .pdf-btn,
  .config-btn,
  .widget-config-btn {
    display: none !important;
  }

  .dashboard-container {
    background: white;
  }

  .grid-layout,
  .kpi-grid {
    gap: 20px;
  }

  .widget-wrapper {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  apexchart {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  body {
    margin: 0;
    padding: 0;
  }
}

.drill-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  font-size: 13px;
  color: #374151;
  background: #f3f4f6;
  border-radius: 6px 6px 0 0;
  margin-bottom: -8px;
  flex: 0 0 auto;
}

.drill-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
}

.drill-mode-switch {
  display: flex;
  gap: 0;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  overflow: hidden;
  margin-left: auto;
}

.drill-mode-switch button {
  background: white;
  border: none;
  padding: 3px 10px;
  cursor: pointer;
  font-size: 12px;
  color: #6b7280;
  transition: all 0.15s;
}

.drill-mode-switch button:hover {
  background: #e5e7eb;
}

.drill-mode-switch button.active {
  background: #0b5394;
  color: white;
}

.drill-crumb {
  background: none;
  border: none;
  color: #0b5394;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 13px;
}

.drill-crumb:hover {
  background: #e5e7eb;
  text-decoration: underline;
}

.drill-sep {
  color: #9ca3af;
}

</style>
